import Button from '../../../components/base/Button';
import { useNavigate } from 'react-router-dom';

export default function ServicesSection() {
  const navigate = useNavigate();

  const handleServiceClick = (serviceTitle: string) => {
    navigate('/services');
    // Attendre que la page se charge puis faire défiler vers la section correspondante
    setTimeout(() => {
      const elements = document.querySelectorAll('h3');
      const targetElement = Array.from(elements).find(el => el.textContent === serviceTitle);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const services = [
    {
      icon: 'ri-camera-line',
      title: 'Vidéo Surveillance',
      description: 'Systèmes de surveillance haute définition avec monitoring 24h/24 et enregistrement sécurisé pour protéger vos locaux.',
      features: ['Caméras HD/4K', 'Vision nocturne', 'Monitoring 24h/24', 'Stockage sécurisé'],
      image: 'https://public.readdy.ai/ai/img_res/35dd0180126c4daf8235f7cd2a3804a3.jpg'
    },
    {
      icon: 'ri-fire-line',
      title: 'Détection Incendie',
      description: "Systèmes de détection précoce et d'alarme incendie conformes aux normes pour assurer la sécurité de vos équipes.",
      features: ['Détection précoce', 'Alarmes sonores', 'Conformité normes', 'Maintenance incluse'],
      image: 'https://static.readdy.ai/image/686bee9df6fcbede85637f4fe190f1d8/0b7797c1b3db31801cb072ca3bfc23bf.jfif'
    },
    {
      icon: 'ri-key-line',
      title: "Contrôle d'Accès",
      description: 'Solutions de contrôle d\'accès intelligentes avec badges, codes et biométrie pour sécuriser vos espaces sensibles.',
      features: ['Badges RFID', "Codes d'accès", 'Biométrie', 'Gestion centralisée'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20access%20control%20system%20with%20electronic%20keypad%20and%20card%20reader%20at%20corporate%20building%20entrance%2C%20professional%20security%20door%20access%2C%20sleek%20modern%20design%2C%20electronic%20access%20control%20technology%2C%20business%20security%20entrance&width=600&height=400&seq=access-control&orientation=landscape'
    },
    {
      icon: 'ri-drop-line',
      title: 'Extinction Automatique',
      description: "Systèmes d'extinction automatique haute performance avec agents gazeux écologiques et efficaces pour une protection maximale de vos équipements sensibles.",
      features: ['Gaz NOVEC 1230', 'Extinction CO2', 'Agent FM200', 'Déclenchement automatique'],
      gasTypes: [
        { name: 'NOVEC 1230', description: 'Agent écologique sans résidu', color: 'bg-green-500' },
        { name: 'CO2', description: 'Extinction rapide et efficace', color: 'bg-blue-500' },
        { name: 'FM200', description: 'Protection équipements sensibles', color: 'bg-purple-500' }
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20automatic%20fire%20suppression%20system%20with%20gas%20cylinders%20in%20modern%20server%20room%2C%20NOVEC%20FM200%20CO2%20gas%20suppression%20equipment%2C%20clean%20technical%20environment%2C%20advanced%20fire%20protection%20technology%2C%20industrial%20safety%20installation&width=600&height=400&seq=gas-suppression&orientation=landscape'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Système Anti-Intrusion',
      description: 'Protection complète contre les intrusions avec détecteurs de mouvement et alarmes pour sécuriser vos biens.',
      features: ['Détecteurs mouvement', 'Alarmes perimetriques', 'Télésurveillance', 'Intervention rapide'],
      image: 'https://static.readdy.ai/image/686bee9df6fcbede85637f4fe190f1d8/be19f4af7638b4a1ac7946be67c5d3e2.jfif'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nos <span className="text-red-600">Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Cobra Sécurité vous propose une gamme complète de solutions de sécurité 
            adaptées aux besoins spécifiques de votre entreprise.
          </p>
        </div>

        {/* Services Grid */}
        <div className="space-y-16">
          {services.map((service, index) => (
            <div key={service.title} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center">
                    <i className={`${service.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">{service.title}</h3>
                </div>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  {service.description}
                </p>
                
                {/* Gas Types for Extinction Automatique */}
                {service.gasTypes && (
                  <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <i className="ri-flask-line text-red-600 mr-2"></i>
                      Agents d'Extinction Disponibles
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {service.gasTypes.map((gas) => (
                        <div key={gas.name} className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow">
                          <div className={`w-12 h-12 ${gas.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                            <i className="ri-drop-fill text-white text-xl"></i>
                          </div>
                          <h5 className="font-bold text-gray-900 mb-1">{gas.name}</h5>
                          <p className="text-sm text-gray-600">{gas.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center space-x-2">
                      <i className="ri-check-line text-red-600 text-lg"></i>
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  variant="outline" 
                  size="md" 
                  className="mt-6"
                  onClick={() => handleServiceClick(service.title)}
                >
                  En Savoir Plus
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
